import React from 'react';
import { Animated, Easing } from 'react-native';

import {
  Box, Inline, BoxSizeOptions, JustifyContentStyleOptions,
} from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import {
  Stack, BodyText,
} from '../../../ui';

import FireIconFilled from '../../../ui/Icons/FireIconFilled';
import { RadialGradientCameraIcon } from '../../../ui/Icons';

export const MAX_BAR_WIDTH = 254;
export const MIN_BAR_WIDTH = 14;
export const BAR_STEP_WIDTH = 40;

type ProgressBarProps = {
  maxValue?: number;
  currentValue?: number;
  disabled?: boolean;
  backgroundColor?: string;
  hideCount?: boolean;
  showExtendBoost?: boolean;
  reverseCountTextColor?: boolean;
  paddingTop?: BoxSizeOptions,
  progressColor?: string;
  progressPosition?: JustifyContentStyleOptions;
  icon?: string;
};

const ProgressBar: React.FC<ProgressBarProps> = ({
  disabled,
  maxValue = 6,
  currentValue = 0,
  backgroundColor = colors.lightGrey,
  hideCount,
  showExtendBoost = false,
  reverseCountTextColor,
  paddingTop = 16,
  progressColor = colors.blue,
  progressPosition = 'center',
  icon,
}) => {
  const progressAnimatedValue = new Animated.Value(MIN_BAR_WIDTH);
  const progressAnimation = Animated.timing(progressAnimatedValue, {
    toValue: Math.min(MAX_BAR_WIDTH, (currentValue * BAR_STEP_WIDTH) + MIN_BAR_WIDTH),
    easing: Easing.out(Easing.cubic),
    duration: 500,
  });

  if (!disabled || showExtendBoost) {
    progressAnimation.start();
  }

  const renderIcon = () => {
    switch (icon) {
      case 'radialCameraIcon':
        return (
          <RadialGradientCameraIcon
            width={16}
            height={16}
          />
        );
      case 'disabledCamera':
        return (
          <RadialGradientCameraIcon
            width={16}
            height={16}
            colorOne={colors.silver}
            colorTwo={colors.silver}
            colorThree={colors.silver}
          />
        );
      default:
        return (
          <FireIconFilled
            color={disabled ? backgroundColor : colors.sunshine}
          />
        );
    }
  };

  return (
    <Stack>
      <Inline justifyContent={progressPosition} alignItems="center" space={8} paddingTop={paddingTop}>
        <Box
          minWidth={MAX_BAR_WIDTH}
          borderRadius={4}
          style={{ overflow: 'hidden' }}
        >
          <Box color={backgroundColor} paddingY={8} />
          {(!disabled || showExtendBoost) && (
            <Animated.View
              style={{
                width: progressAnimatedValue,
                position: 'absolute',
              }}
              testID="search-boost-animation-progress"
            >
              <Box
                color={progressColor}
                paddingY={8}
              />
            </Animated.View>
          )}
        </Box>
        {renderIcon()}
      </Inline>
      {!hideCount && !showExtendBoost && (
        <BodyText
          textAlign="center"
          color={disabled || reverseCountTextColor ? backgroundColor : colors.slate}
          appearance="legal"
          weight="bold"
          style={{
            paddingTop: 16,
          }}
        >
          {`${currentValue}/${maxValue} Appointments Booked`}
        </BodyText>
      )}
    </Stack>
  );
};

export default ProgressBar;
